import { useCallback, useContext, useEffect, useState } from "react";
import { BotContext } from "../store";
import { axiosGet, axiosPost } from "@/services/axiosService";
import { CharacterLimitIcon } from "./AiBotAssets";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { State } from "@/redux/Types";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { userSlice } from "@/redux/User";
import useFormatNumber from "@/hooks/UseFormatNumber";
import ModalWrapper from "./ModalWrapper";
import BooleanModal from "@/components/builder/BooleanModal";
import { debounce } from "lodash";

interface TextAreaComponentProps {
  selectedSourceTab: string;
  textContentId: string;
  characterUsed: number;
  characterLimit: number;
  allTrained: boolean;
  onDataChange?: (key: string, value: any) => void;
  isOld?: boolean;
}

export default function TextAreaComponent({
  selectedSourceTab,
  textContentId,
  characterUsed,
  characterLimit,
  allTrained,
  onDataChange,
  isOld,
}: TextAreaComponentProps) {
  const [store, storeDispatch] = useContext(BotContext);
  const [loader, setLoader] = useState(true);
  const dispatch = useDispatch();
  const router = useNavigate();
  const plan: any = useSelector((state: State) => state.user.plan);
  const [leaveModal, setLeaveModal] = useState(false);
  const [buyAddOn, setBuyAddOn] = useState(false);

  useEffect(() => {
    async function fetchTextArea() {
      try {
        if (store.textAreaContent !== null || !textContentId) return;
        setLoader(true);
        const response = await axiosGet(`/ai/content/${textContentId}`);
        storeDispatch({
          type: "set-textAreaContent",
          textAreaContent:
            response.data.data.draftContent ?? response.data.data.content,
        });
        storeDispatch({
          type: "set-textAreaContentPrev",
          textAreaContent: response.data.data?.content,
        });
      } catch (err) {
        console.log(err);
      } finally {
        setLoader(false);
      }
    }

    fetchTextArea();
  }, [store.textAreaContent, textContentId]);

  const autoSave = useCallback(
    debounce(async (content) => {
      try {
        storeDispatch({
          type: "set-textAreaStatus",
          textAreaStatus: true,
        });
        const data = await axiosPost("/ai/content", {
          id: textContentId,
          draftContent: content,
          botCustomId: store?.botData?.botCustomId,
        });
        storeDispatch({
          type: "set-textAreaContent",
          textAreaContent: content,
        });
        storeDispatch({
          type: "set-textAreaStatus",
          textAreaStatus: false,
        });
        if (onDataChange && !store?.botData?.aiSource?.textArea?.contentId) {
          onDataChange("aiSource", {
            ...(store?.botData?.aiSource || {}),
            textArea: { contentId: data?.data?.data?._id },
          });
        }
      } catch (err) {}
    }, 1000),
    [textContentId, store?.botData?.botCustomId, store?.botData?.botCreatedAt],
  );

  const handleTextAreaChange = (e) => {
    const newValue = e.target.value;

    storeDispatch({
      type: "set-textAreaContent",
      textAreaContent: newValue,
    });

    autoSave(newValue);
  };

  return (
    <div
      className={`fx-d-col h-100 textarea_tab flex ${selectedSourceTab === "trained_files" ? "mb-[30px] min-h-[120px]" : ""}`}
    >
      <div className="flex items-center justify-between">
        <h3 className="mb-0 flex items-center gap-[10px]">
          Agent Text Area
          {!allTrained && isOld && (
            <div className="character-count-wrapper flex items-center gap-[10px]">
              <span className="!ms-[0px] h-[24px] w-[1px] bg-[#666] opacity-[0.3]"></span>
              <p
                className={`character-count pointer relative z-[5] flex items-center gap-[4px] rounded-[5px] px-[8px] py-[6px] text-[13px] font-medium capitalize leading-normal ${useFormatNumber(characterUsed) === useFormatNumber(characterLimit) ? "bg-[#FFE8E9] text-[#fb5f66]" : "bg-[#FEF2CC] text-[#9A6815]"} `}
              >
                <CharacterLimitIcon
                  color={
                    useFormatNumber(characterUsed) ===
                    useFormatNumber(characterLimit)
                      ? "#fb5f66"
                      : "#9A6815"
                  }
                />
                {characterLimit === 0 ? (
                  <div className="yellow-loader"></div>
                ) : (
                  `${useFormatNumber(characterUsed)} / ${useFormatNumber(characterLimit)}`
                )}
                <div className="none absolute left-[0] top-[35px] z-[1] w-[280px] cursor-default flex-col items-center gap-[10px] rounded-[8px] bg-[#222] p-[10px]">
                  {useFormatNumber(characterUsed) ===
                  useFormatNumber(characterLimit) ? (
                    <p className="text-center text-[12px] font-normal normal-case not-italic leading-[17px] text-[#fb5f66]">
                      Your current plan doesn't support more than
                      <b className="font-medium">
                        {" "}
                        {useFormatNumber(characterLimit)} Characters.{" "}
                      </b>
                      {plan.name !== "Professional"
                        ? "Upgrade to the Professional plan to unlock this feature."
                        : "If you want more, you have to buy the add-on."}
                    </p>
                  ) : (
                    <p className="text-center text-[12px] font-normal normal-case not-italic leading-[17px] text-white/80">
                      Agents can use a maximum of <br />
                      <b className="font-medium">
                        {useFormatNumber(characterLimit)} Characters
                      </b>
                      , including link content. Anything over that limit won't
                      be used.
                    </p>
                  )}
                  {plan.name !== "Professional" ? (
                    <button
                      className="pointer h-[30px] w-full rounded-[8px] bg-[#fb5f66] text-[12px] font-normal leading-normal text-white"
                      onClick={() => {
                        setBuyAddOn(false);
                        setLeaveModal(true);
                      }}
                    >
                      Upgrade Plan
                    </button>
                  ) : (
                    <button
                      className="pointer h-[30px] w-full rounded-[8px] bg-[#fb5f66] text-[12px] font-normal leading-normal text-white"
                      onClick={() => {
                        setBuyAddOn(true);
                        setLeaveModal(true);
                      }}
                    >
                      Buy add-on
                    </button>
                  )}
                </div>
              </p>
            </div>
          )}
        </h3>
        {selectedSourceTab == "text" && (
          <p className={`tarea_length`}>
            {/* {wordCount + 1}/{TEXT_AREA_LIMIT - 1} words */}
          </p>
        )}
      </div>

      <div className="h-100 bot_tarea rightSidebar-tarea-wrapper flex flex-col gap-[10px] !bg-[#f8f8f8] pr-[3px]">
        {loader ? (
          <>
            <Skeleton
              height={30}
              count={5}
              baseColor="#8c8c8c2b"
              highlightColor="#ffffff"
              duration={2}
            />
          </>
        ) : (
          <textarea
            readOnly={selectedSourceTab === "trained_files"}
            className={`h-100 grey-scrollbar rightSidebar-tarea grey-scrollbar resize-none !bg-[#f8f8f8] ${selectedSourceTab === "trained_files" ? "min-h-[100px]" : "min-h-[250px]"}`}
            placeholder="Type text you would like your agent to get trained on..."
            value={store.textAreaContent ?? ""}
            onChange={handleTextAreaChange}
          ></textarea>
        )}
        {/* {wordCount == TEXT_AREA_LIMIT - 1 && (
      <p className="flex items-center gap-[6px] text-[12px] text-[#fb5f66]">
        <svg
          width={15}
          height={14}
          viewBox="0 0 15 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g clipPath="url(#clip0_1344_48051)">
            <path
              d="M14.3029 10.4994L9.35734 1.40823C8.56275 0.0705426 6.62581 0.0687653 5.83016 1.40823L0.884876 10.4994C0.0726023 11.8663 1.05602 13.5972 2.64806 13.5972H12.5393C14.13 13.5972 15.1151 11.8676 14.3029 10.4994ZM7.59375 11.9565C7.14154 11.9565 6.77343 11.5884 6.77343 11.1362C6.77343 10.684 7.14154 10.3159 7.59375 10.3159C8.04596 10.3159 8.41406 10.684 8.41406 11.1362C8.41406 11.5884 8.04596 11.9565 7.59375 11.9565ZM8.41406 8.67529C8.41406 9.1275 8.04596 9.4956 7.59375 9.4956C7.14154 9.4956 6.77343 9.1275 6.77343 8.67529V4.57373C6.77343 4.12152 7.14154 3.75342 7.59375 3.75342C8.04596 3.75342 8.41406 4.12152 8.41406 4.57373V8.67529Z"
              fill="#FB5F66"
            />
          </g>
          <defs>
            <clipPath id="clip0_1344_48051">
              <rect
                width={14}
                height={14}
                fill="white"
                transform="translate(0.59375)"
              />
            </clipPath>
          </defs>
        </svg>
        Word limit exceeded
      </p>
    )} */}
      </div>
      {leaveModal && (
        <ModalWrapper>
          <BooleanModal
            onConfirm={() => {
              router("/settings");
              buyAddOn
                ? dispatch(userSlice.actions.setSettings(6))
                : dispatch(userSlice.actions.setSettings(2));
            }}
            onCancel={() => setLeaveModal(false)}
          />
        </ModalWrapper>
      )}
    </div>
  );
}
